import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper";
import "swiper/css";
import "swiper/css/bundle";
import {
  FilteredProductState,
  GlobalStyleState,
  ProductKindFilterState,
  ProductState,
  SelectedProductState,
} from "../store/chart";
import styled, { useTheme } from "styled-components";
import { Text } from "./Text";
import { FontWeight } from "../types";
import { Tag } from "./Tag";
import { ReactComponent as ArrowIcon } from "../assets/images/arrow.svg";
import { ReactComponent as AmpIcon } from "../assets/images/amp.svg";
import { ReactComponent as CloseIcon } from "../assets/images/close.svg";
import Image from "./Image";

type CarouselProps = {
  className?: string;
  filteredProducts: FilteredProductState[];
  selectedProducts: SelectedProductState[];
  showPrice: boolean;
  styling: GlobalStyleState;
  onProductToggle: (productId: string) => void;
};

export function Carousel({
  className,
  filteredProducts,
  selectedProducts,
  styling,
  onProductToggle,
}: CarouselProps) {
  const { getValue } = useTheme();

  const shouldShowTag = filteredProducts.reduce((acc, filteredProduct) => {
    const shouldShowTag =
      filteredProduct.products.filter(
        (product) => product.useTag && product.tag?.text,
      ).length > 0;
    return acc || shouldShowTag;
  }, false);

  const slides = filteredProducts.flatMap((filteredProduct, filteredIndex) =>
    filteredProduct.products.map((product, productIndex) => (
      <SwiperSlide
        key={product.id}
        style={{
          width:
            productIndex === 0 && filteredIndex > 0
              ? `${getValue(296)}px`
              : `${getValue(260)}px`,
        }}
      >
        <Product
          showCategoryTitle={productIndex === 0}
          showCategoryDivider={productIndex === 0 && filteredIndex > 0}
          productKindFilter={filteredProduct.filter}
          product={product}
          onToggle={onProductToggle}
          selected={
            selectedProducts.find(
              (selectedProduct) => selectedProduct.id === product.id,
            )?.selected
          }
          showTag={shouldShowTag}
          styling={styling}
        />
      </SwiperSlide>
    )),
  );

  return (
    <StyledSwiper>
      <Swiper
        className={className}
        slidesPerView={"auto"}
        spaceBetween={getValue(36)}
        navigation={{
          prevEl: ".custom-button-prev",
          nextEl: ".custom-button-next",
        }}
        modules={[Navigation]}
        noSwipingClass="product-wrapper"
        slidesPerGroup={2}
      >
        {slides}
      </Swiper>
      <button className="custom-button-prev">
        <ArrowIcon />
      </button>
      <button className="custom-button-next">
        <ArrowIcon />
      </button>
    </StyledSwiper>
  );
}

type ProductProps = {
  showCategoryTitle: boolean;
  showCategoryDivider: boolean;
  productKindFilter: ProductKindFilterState;
  product: ProductState;
  selected?: boolean;
  showTag: boolean;
  styling: GlobalStyleState;
  onToggle: (productId: string) => void;
};

function Product({
  showCategoryTitle,
  showCategoryDivider,
  productKindFilter,
  product,
  selected,
  showTag,
  styling,
  onToggle,
}: ProductProps) {
  let ProductContainerComponent = ProductContainer;
  if (!showTag) {
    ProductContainerComponent = ProductContainerProductOnly;
  }
  return (
    <ProductWrapper className="product-wrapper" divider={showCategoryDivider}>
      <CategoryTitle visible={showCategoryTitle}>
        {productKindFilter.categoryTitle}
      </CategoryTitle>
      <ProductContainerComponent
        selected={selected}
        onClick={() => onToggle(product.id)}
      >
        <ProductUnselectButton visible={selected}>
          <CloseIcon />
        </ProductUnselectButton>
        {product.isBundle ? (
          <ProductRelatedImageWrapper>
            <ProductRelatedImage>
              <ProductImage src={product.leftData.image} />
            </ProductRelatedImage>
            <ProductImageSeparator>
              <AmpIcon />
            </ProductImageSeparator>
            <ProductRelatedImage>
              <ProductImage src={product.rightData.image} />
            </ProductRelatedImage>
          </ProductRelatedImageWrapper>
        ) : (
          <ProductImageWrapper>
            <ProductImage src={product.data.image} alt={product.name} />
          </ProductImageWrapper>
        )}
        {showTag && (
          <TagWrapper>
            {product.useTag && product.tag && (
              <Tag tag={product.tag} styling={styling} />
            )}
          </TagWrapper>
        )}
        <ProductName>{product.name}</ProductName>
      </ProductContainerComponent>
    </ProductWrapper>
  );
}

const StyledSwiper = styled.div`
  position: relative;

  .custom-button-prev,
  .custom-button-next {
    position: absolute;
    top: 50%;
    display: flex;
    justify-content: center;
    align-items: center;
    height: ${({ theme }) => theme.getValue(60)}px;
    width: ${({ theme }) => theme.getValue(60)}px;
    padding: 0;
    opacity: 1;
    border: 0;
    border-radius: 50%;
    z-index: 99;
    transform: rotate(0) translateY(-100%);
    transition: opacity 0.15s linear;
    background-color: ${({ theme }) => theme.colors.green2};
    background-size: ${({ theme }) => theme.getValue(24)}px
      ${({ theme }) => theme.getValue(24)}px;
    appearance: none;

    &:after {
      content: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.colors.green1};
      &:active {
        transition: none;
        opacity: 0.75;
      }
    }

    &.swiper-button-disabled {
      opacity: 0;

      &:hover {
        cursor: default;
      }
    }

    svg {
      width: ${({ theme }) => theme.getValue(22)}px;
      height: ${({ theme }) => theme.getValue(22)}px;
    }
  }

  .custom-button-prev {
    left: -${({ theme }) => theme.getValue(86)}px;
    transform: rotate(180deg) translateY(100%);
  }
  .custom-button-next {
    right: -${({ theme }) => theme.getValue(86)}px;
  }
`;

const ProductWrapper = styled.div<{ divider: boolean }>`
  display: grid;
  grid-template-rows: ${({ theme }) => theme.getValue(84)}px 1fr;
  min-width: ${({ theme }) => theme.getValue(260)}px;
  padding-left: ${({ theme, divider }) =>
    divider ? `${theme.getValue(36)}px` : 0};
  border-left: ${({ theme, divider }) =>
    divider ? `1px solid ${theme.colors.grey1}` : 0};
`;

const CategoryTitle = styled(Text)<{ visible: boolean }>`
  margin: 0;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};

  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(30)}px;
    line-height: 1;
  }
`;

const ProductContainer = styled.div<{ selected?: boolean }>`
  position: relative;
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-rows: ${({ theme }) =>
    `${theme.getValue(200)}px
    ${theme.getValue(72)}px
    ${theme.getValue(80)}px`};
  padding: ${({ theme }) => theme.getValue(24)}px;
  border-radius: ${({ theme }) => theme.getValue(30)}px;
  border: ${({ theme, selected }) =>
    `${theme.getValue(6)}px solid ${
      selected ? theme.colors.green2 : "transparent"
    };`};
  cursor: pointer;
  transition: all 0.2s;
`;

const ProductContainerProductOnly = styled(ProductContainer)`
  grid-auto-rows: ${({ theme }) =>
    `${theme.getValue(200)}px
     ${theme.getValue(80)}px`};
`;

const ProductUnselectButton = styled.button<{ visible?: boolean }>`
  position: absolute;
  top: -${({ theme }) => theme.getValue(16)}px;
  right: -${({ theme }) => theme.getValue(6)}px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.getValue(32)}px;
  height: ${({ theme }) => theme.getValue(32)}px;
  min-width: 18px;
  min-height: 18px;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  border: 0;
  background: ${({ theme }) => theme.colors.green2};
  appearance: none;
  visibility: ${({ visible }) => (visible ? "visible" : "hidden")};

  svg {
    width: ${({ theme }) => theme.getValue(10)}px;
    height: ${({ theme }) => theme.getValue(10)}px;
    min-width: 6px;
    min-height: 6px;
  }
`;

const ProductImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: ${({ theme }) => theme.getValue(200)}px;
  height: ${({ theme }) => theme.getValue(200)}px;
  margin: 0 auto;
`;

const ProductRelatedImageWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: ${({ theme }) => theme.getValue(200)}px;
  margin: 0 auto;
`;

const ProductRelatedImage = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-end;
  width: ${({ theme }) => theme.getValue(100)}px;
  height: ${({ theme }) => theme.getValue(100)}px;
`;

const ProductImage = styled(Image).attrs(({ responsiveWidth }) => ({
  responsiveWidth: responsiveWidth || 300,
}))`
  display: block;
  width: 100%;
  height: auto;
  max-width: 100%;
  max-height: 100%;
`;

const ProductImageSeparator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme }) => theme.getValue(40)}px;
  height: ${({ theme }) => theme.getValue(40)}px;
  transform: translate(-50%, -50%);
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.green5};

  svg {
    width: ${({ theme }) => theme.getValue(20)}px;
    height: ${({ theme }) => theme.getValue(24)}px;
  }
`;

const ProductName = styled(Text)`
  &&& {
    font-weight: ${({ font: { weight = FontWeight.Bold } = {} }) => weight};
    font-size: ${({ theme }) => theme.getFontSize(22)}px;
    line-height: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 ${({ theme }) => theme.getValue(16)}px;
    text-align: center;
  }
`;

const TagWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;
